import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Button, Card, CardBody, CardHeader, Link, Spinner, Switch } from '@structural-climbing/ui'
import { StripeBalanceCard } from './StripeBalanceCard'
import { FeeExamples, type FeeExample } from './FeeExamples'
import {
  MemberPaymentJourney,
  type JourneyBranding,
  type JourneyPlan,
} from './MemberPaymentJourney'
import { PLATFORM_CAP_LEI, PLATFORM_RATE, SAMPLE_PRICES } from './paymentFees'
import type { GymPlan } from '../plans/types'

interface AccountStatus {
  connected: boolean
  charges_enabled: boolean
  payouts_enabled: boolean
  payments_enabled: boolean
  /**
   * Whose move it is while the account can't take money yet. Absent from a
   * Worker older than this app (they deploy separately), which is read as
   * action_needed: the message we always showed before.
   */
  review_state?: 'ready' | 'in_review' | 'action_needed' | 'blocked'
}

/**
 * Where an owner connects their Stripe account and turns online payment on.
 *
 * This lives beside GymSettingsForm rather than inside it because the form's
 * set() helper only carries strings, and because connecting is an action with
 * its own round trip rather than a field that gets saved with the rest.
 *
 * The switch stays locked until Stripe says the account can actually take
 * money. Letting an owner flip it earlier would put a Pay button in front of
 * members that could only fail.
 */
const FALLBACK_BRANDING: JourneyBranding = {
  name: 'Structural',
  image_url: null,
  cover_url: null,
  primary_color: null,
  secondary_color: null,
}

/**
 * The plans worth showing as examples: what members could actually buy.
 * Plans already marked payable online come first; a gym that has not marked
 * any yet sees its ordinary main plans, which is what it will be choosing from.
 */
function examplePlans(plans: GymPlan[]): GymPlan[] {
  const main = plans
    .filter((p) => p.is_active && !p.is_addon && !p.external_provider && p.price > 0)
    .sort((a, b) => a.price - b.price)
  const payable = main.filter((p) => p.payable_online)
  return payable.length > 0 ? payable : main
}

export function PaymentsSection({ branding }: { branding?: JourneyBranding }) {
  const { getAccessTokenSilently } = useAuth()
  const { t, i18n } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  /** "1,50 lei" in Romanian, "1.50 lei" in English, for the copy. */
  const lei = (amount: number, digits: number) =>
    `${new Intl.NumberFormat(i18n.language.startsWith('ro') ? 'ro-RO' : 'en-GB', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(amount)} lei`

  // Only for the examples. Failing to load them costs the gym's own names and
  // prices, never the tab: sample plans stand in.
  const [gymPlans, setGymPlans] = useState<GymPlan[]>([])
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${apiUrl}/gym-plans`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) return
        const body = (await res.json()) as unknown
        if (!cancelled && Array.isArray(body)) setGymPlans(body as GymPlan[])
      } catch {
        /* samples stand in */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [apiUrl, getAccessTokenSilently])

  const [status, setStatus] = useState<AccountStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [connecting, setConnecting] = useState(false)
  const [savingSwitch, setSavingSwitch] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/stripe/account-status`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('status')
      setStatus((await res.json()) as AccountStatus)
    } catch {
      setError(t('payments.errorLoad'))
    } finally {
      setLoading(false)
    }
  }, [apiUrl, getAccessTokenSilently, t])

  useEffect(() => {
    void load()
  }, [load])

  async function handleConnect() {
    setConnecting(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/stripe/connect-onboarding`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      })
      if (!res.ok) throw new Error('connect')
      const { url } = (await res.json()) as { url: string }
      // Stripe hosts onboarding; the link is single-use and short-lived, so we
      // leave immediately rather than holding it.
      window.location.assign(url)
    } catch {
      setError(t('payments.errorConnect'))
      setConnecting(false)
    }
  }

  async function handleToggle(next: boolean) {
    setSavingSwitch(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/gym-settings`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ payments_enabled: next }),
      })
      if (!res.ok) throw new Error('toggle')
      setStatus((prev) => (prev ? { ...prev, payments_enabled: next } : prev))
    } catch {
      setError(t('payments.errorSave'))
    } finally {
      setSavingSwitch(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    )
  }

  const canAcceptPayments = status?.charges_enabled === true
  const reviewState = status?.review_state ?? 'action_needed'

  const brand = branding ?? FALLBACK_BRANDING
  const accent = brand.primary_color ?? '#BD4627'
  const real = examplePlans(gymPlans)
  const currency = real[0]?.currency ?? 'RON'

  const journeyPlans: JourneyPlan[] =
    real.length > 0
      ? real
      : [
          {
            name: t('payments.samplePlanDay'),
            price: 50,
            plan_type: 'day_pass',
            max_entries: null,
            duration_days: 1,
          },
          {
            name: t('payments.samplePlanMonth'),
            price: 275,
            plan_type: 'duration_based',
            max_entries: null,
            duration_days: 30,
          },
          {
            name: t('payments.samplePlanTen'),
            price: 400,
            plan_type: 'entry_based',
            max_entries: 10,
            duration_days: 90,
          },
        ].map((p) => ({ ...p, currency }) as JourneyPlan)

  // One row per distinct price, so two plans at the same price don't repeat
  // the same sum.
  const seen = new Set<number>()
  const ownExamples: FeeExample[] = real
    .filter((p) => !seen.has(p.price) && seen.add(p.price))
    .slice(0, 4)
    .map((p) => ({ label: p.name, price: p.price }))
  const feeExamples: FeeExample[] =
    ownExamples.length >= 2 ? ownExamples : SAMPLE_PRICES.map((price) => ({ label: null, price }))

  const connectButton = (
    <div className="flex flex-col items-start gap-2">
      <Button color="primary" isLoading={connecting} onClick={handleConnect}>
        {t('payments.connectButton')}
      </Button>
      <p className="text-xs text-foreground/50">{t('payments.connectHint')}</p>
    </div>
  )

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <h2 className="text-base font-semibold">{t('payments.sectionTitle')}</h2>
        </CardHeader>
        <CardBody className="flex flex-col gap-4">
          <p className="text-sm text-foreground/60">{t('payments.intro')}</p>

          {error && <p className="text-sm text-danger">{error}</p>}

          {!status?.connected && connectButton}

          {/*
            Stripe reviewing and Stripe waiting on the owner used to share one
            "still needs a few details" message, which sent owners who had
            finished back through the form. Each state now says whose move it is.
          */}
          {status?.connected && !canAcceptPayments && reviewState === 'in_review' && (
            <div className="flex flex-col items-start gap-2">
              <p className="text-sm font-medium">{t('payments.statusInReview')}</p>
              <p className="text-xs text-foreground/50">{t('payments.statusInReviewHint')}</p>
              <Button variant="flat" isLoading={connecting} onClick={handleConnect}>
                {t('payments.reviewDetailsButton')}
              </Button>
            </div>
          )}

          {status?.connected && !canAcceptPayments && reviewState === 'blocked' && (
            <div className="flex flex-col items-start gap-2">
              <p className="text-sm font-medium text-danger">{t('payments.statusBlocked')}</p>
              <p className="text-xs text-foreground/50">{t('payments.statusBlockedHint')}</p>
              <Link href="https://dashboard.stripe.com" isExternal showAnchorIcon size="sm">
                {t('payments.openStripeDashboard')}
              </Link>
            </div>
          )}

          {status?.connected &&
            !canAcceptPayments &&
            reviewState !== 'in_review' &&
            reviewState !== 'blocked' && (
              <div className="flex flex-col items-start gap-2">
                <p className="text-sm font-medium text-warning">{t('payments.statusIncomplete')}</p>
                <p className="text-xs text-foreground/50">{t('payments.statusIncompleteHint')}</p>
                <Button color="primary" isLoading={connecting} onClick={handleConnect}>
                  {t('payments.continueButton')}
                </Button>
              </div>
            )}

          {status?.connected && canAcceptPayments && (
            <>
              <p className="text-sm font-medium text-success">{t('payments.statusReady')}</p>
              {!status.payouts_enabled && (
                <p className="text-xs text-warning">{t('payments.payoutsPending')}</p>
              )}
              <Switch
                isSelected={status.payments_enabled}
                isDisabled={savingSwitch}
                onValueChange={handleToggle}
              >
                <div className="text-sm">
                  {t('payments.acceptToggle')}
                  <p className="text-xs text-foreground/50">{t('payments.acceptToggleHint')}</p>
                </div>
              </Switch>
              <StripeBalanceCard />
            </>
          )}
        </CardBody>
      </Card>

      {/*
        What the member gets, drawn in this gym's colours with its own plans.
        An owner deciding whether to switch this on should see the thing their
        members will tap, not read a description of it.
      */}
      <Card>
        <CardHeader className="flex flex-col items-start gap-1">
          <h2 className="text-base font-semibold">{t('payments.howTitle')}</h2>
          <p className="text-sm text-foreground/60">{t('payments.howIntro')}</p>
        </CardHeader>
        <CardBody className="pb-6">
          <MemberPaymentJourney branding={brand} plans={journeyPlans} />
        </CardBody>
      </Card>

      {/*
        Both cuts, named, before the owner connects anything, worked through on
        the gym's own prices. Stripe's share lands on their own dashboard, so
        the split here is one they can check rather than take on trust. The
        rates live in paymentFees.ts and must move with the Worker's.
      */}
      <Card>
        <CardHeader className="flex flex-col items-start gap-1">
          <h2 className="text-base font-semibold">{t('payments.feesTitle')}</h2>
          <p className="text-sm text-foreground/60">
            {t('payments.feesStripe')} {t('payments.feesPlatform')}
          </p>
        </CardHeader>
        <CardBody className="flex flex-col gap-4">
          <FeeExamples examples={feeExamples} currency={currency} accent={accent} />
          <p className="text-xs text-foreground/50">
            {t('payments.feesCapNote', {
              from: lei(PLATFORM_CAP_LEI / PLATFORM_RATE, 0),
              cap: lei(PLATFORM_CAP_LEI, 2),
            })}
          </p>
          {!status?.connected && connectButton}
        </CardBody>
      </Card>
    </div>
  )
}
